import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Swal from "sweetalert2";
import { Profile } from "../../services/profileCalls";
import { DoneBooking, ProfileProps } from "../../types/shared";

interface InitProps {
  profile: ProfileProps[];
  doneBookingsShow: DoneBooking[];
  loading: boolean;
  error: string;
  success: boolean;
}

const initialState: InitProps = {
  profile: [],
  doneBookingsShow: [],
  loading: false,
  error: "",
  success: false,
};

export const getTourProfile = createAsyncThunk(
  "profile/getToursProfile",
  async (token: string | null, { rejectWithValue }) => {
    try {
      const response = await Profile.getReserveProfile(token);
      return response.data;
    } catch {
      return rejectWithValue(
        "Ocurrio un error inesperado, intente nuevamente mas tarde."
      );
    }
  }
);

export const doneBookings = createAsyncThunk(
  "profile/doneBookings",
  async (token: string | null, { rejectWithValue }) => {
    try {
      const response = await Profile.getDoneBookings();

      const bookingsDone: DoneBooking[] = [];
      Object.entries(response.data).forEach(([key, value]) => {
        bookingsDone.push({ title: key, bookings: value as ProfileProps[] });
      });
      return bookingsDone;
    } catch {
      return rejectWithValue(
        "Ocurrio un error inesperado, intente nuevamente mas tarde."
      );
    }
  }
);

export const deleteAlertSuscription = createAsyncThunk(
  "profile/deleteAlertSuscription",
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await Profile.deleteAlertSuscription(email);
      return response.data;
    } catch {
      return rejectWithValue(
        "Ocurrio un error inesperado, intente nuevamente mas tarde."
      );
    }
  }
);

export const suscribeAlert = createAsyncThunk(
  "profile/suscribeAlert",
  async (token: string | null, { rejectWithValue }) => {
    try {
      const response = await Profile.suscribeAlert();
      return response.data;
    } catch {
      return rejectWithValue(
        "Ocurrio un error inesperado, intente nuevamente mas tarde."
      );
    }
  }
);

export const deleteReserveProfile = createAsyncThunk(
  "profile/deleteReserveProfile",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await Profile.deleteReserveProfile(id);
      if (response.status !== 200) {
        return rejectWithValue(response.data);
      }
      Swal.fire("Cancelado!", "Se cancelo correctamente.", "success");
      return response;
    } catch {
      return rejectWithValue(
        "Ocurrio un error inesperado, intente nuevamente mas tarde."
      );
    }
  }
);

export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getTourProfile.pending, state => {
        state.loading = true;
        state.error = "";
      })
      .addCase(getTourProfile.fulfilled, (state, action: any) => {
        const data = Array.isArray(action.payload) ? action.payload : [];
        const nextBookings = data.filter((el: any) => {
        // Convierte a formato ISO para compatibilidad cross-browser
        const dateTimeISO = `${el.date}T${el.hour.length === 5 ? el.hour : el.hour.slice(0,5)}:00`;
        const tourTime = new Date(dateTimeISO).getTime();
        const now = new Date().getTime();
        return tourTime + 3600000 > now;
        });
        state.loading = false;
        state.error = "";
        state.profile = nextBookings.sort(
        (a: any, b: any) =>
          new Date(`${a.date}T${a.hour.length === 5 ? a.hour : a.hour.slice(0,5)}:00`).getTime() -
          new Date(`${b.date}T${b.hour.length === 5 ? b.hour : b.hour.slice(0,5)}:00`).getTime()
        );
      })
      .addCase(getTourProfile.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(doneBookings.pending, state => {
        state.loading = true;
        state.error = "";
      })
      .addCase(doneBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.doneBookingsShow = action.payload;
        state.error = "";
      })
      .addCase(doneBookings.rejected, state => {
        state.loading = true;
        state.error = "";
      })
      .addCase(deleteReserveProfile.pending, state => {
        state.loading = true;
        state.error = "";
        state.success = false;
      })
      .addCase(deleteReserveProfile.fulfilled, state => {
        state.loading = false;
        state.error = "";
        state.success = true;
      })
      .addCase(deleteReserveProfile.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
        state.success = false;
      })
      .addCase(deleteAlertSuscription.pending, state => {
        state.loading = true;
        state.error = "";
        state.success = false;
      })
      .addCase(deleteAlertSuscription.fulfilled, state => {
        state.loading = false;
        state.error = "";
        state.success = true;
      })
      .addCase(deleteAlertSuscription.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
        state.success = false;
      });
  },
});

export default profileSlice.reducer;
